/**
 * Gemini Live との音声セッションを管理する。
 *
 * React の外に置いてある。AudioContext / WebSocket / AudioWorklet は
 * 再レンダリングで作り直されると壊れるため、state には決して入れないこと。
 * React からは useRef 経由で 1 インスタンスだけ持ち、イベントを購読する。
 */

import {
  INPUT_SAMPLE_RATE as INPUT_RATE,
  OUTPUT_SAMPLE_RATE as OUTPUT_RATE,
  RESPONSE_TIMEOUT_MS,
} from "@app/shared";
import type { Turn, TokenResponse } from "@app/shared";

export type EngineState =
  | "idle"
  | "connecting"
  | "ready"
  | "listening"
  | "thinking"
  | "speaking"
  | "ended"
  | "error";

export type EngineEvents = {
  stateChange: (state: EngineState) => void;
  /** 文字起こしの部分更新。role ごとに現在の発話が伸びていく */
  transcriptUpdate: (role: "user" | "assistant", text: string) => void;
  /** 1往復ぶんの発話が確定した */
  turnComplete: (turn: Turn) => void;
  /** 往復数が変わった */
  turnCountChange: (count: number) => void;
  latency: (ms: number) => void;
  /** 会話が終了した。reason は終了のきっかけ */
  ended: (reason: "max_turns" | "user" | "error" | "disconnected") => void;
  error: (message: string) => void;
  /** 実機診断用のログ行。?debug=1 のときだけ画面に出す。音声不達の切り分けに使う */
  debug: (line: string) => void;
};

type Listener<K extends keyof EngineEvents> = EngineEvents[K];

export type EngineOptions = {
  maxTurns: number;
  /** 短命トークンを取りに行く。サーバが setup も返す */
  fetchToken: () => Promise<TokenResponse>;
};

export class ConversationEngine {
  private opts: EngineOptions;
  private ctx: AudioContext | null = null;
  private ws: WebSocket | null = null;
  private stream: MediaStream | null = null;
  private worklet: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private sink: GainNode | null = null;

  private listeners: { [K in keyof EngineEvents]?: Set<Listener<K>> } = {};

  private _state: EngineState = "idle";
  private sending = false;
  private releasedAt = 0;
  private awaitingFirstAudio = false;
  private playHead = 0;
  private responseTimer: number | null = null;

  private turnIndex = 0;
  private userBuffer = "";
  private assistantBuffer = "";
  private turns: Turn[] = [];
  /** このターンで受信した音声チャンク数。turnComplete でログして 0 に戻す */
  private audioChunksThisTurn = 0;

  constructor(opts: EngineOptions) {
    this.opts = opts;
  }

  /** 実機診断ログ。listener が無ければ何もしない（本番は ?debug=1 のときだけ購読） */
  private dbg(line: string): void {
    this.emit("debug", line);
  }

  // ---- events -------------------------------------------------------------

  on<K extends keyof EngineEvents>(event: K, fn: Listener<K>): () => void {
    (this.listeners[event] ??= new Set() as never).add(fn as never);
    return () => this.off(event, fn);
  }

  off<K extends keyof EngineEvents>(event: K, fn: Listener<K>): void {
    this.listeners[event]?.delete(fn as never);
  }

  private emit<K extends keyof EngineEvents>(
    event: K,
    ...args: Parameters<Listener<K>>
  ): void {
    this.listeners[event]?.forEach((fn) => {
      try {
        (fn as (...a: unknown[]) => void)(...args);
      } catch (e) {
        console.error("listener error", e);
      }
    });
  }

  get state(): EngineState {
    return this._state;
  }

  get currentTurn(): number {
    return this.turnIndex;
  }

  get transcript(): Turn[] {
    return [...this.turns];
  }

  private setState(s: EngineState): void {
    if (this._state === s) return;
    this._state = s;
    this.emit("stateChange", s);
  }

  // ---- lifecycle ----------------------------------------------------------

  /**
   * 必ずユーザー操作（クリック等）から呼ぶこと。
   * iOS では AudioContext が suspended で生成されるため、
   * ユーザージェスチャの文脈でないと resume できない。
   */
  async start(): Promise<void> {
    if (this._state !== "idle" && this._state !== "error") return;
    this.setState("connecting");

    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.ctx = new Ctor();
      this.dbg(`ctx作成 state=${this.ctx.state} rate=${this.ctx.sampleRate}`);
      await this.ctx.resume();
      this.dbg(`resume後 state=${this.ctx.state}`);

      // iOS の初回再生は 546ms かかる。無音を 1 回鳴らして温めておく。
      this.warmUp();

      // echoCancellation は切る。Android では AEC が音声セッションを通話モードにし、
      // 本体スピーカーの再生音を「エコー」とみなして打ち消す（＝スピーカーで無音）。
      // 本アプリはプッシュトゥトークの半二重で、AI 発話中はマイク送信しないため
      // AEC はほぼ不要。切ることでスピーカー出力が生きる。
      // （Bluetooth ではマイクと出力が離れており AEC が効いても聞こえていた）
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      const s = this.stream.getAudioTracks()[0]?.getSettings?.() ?? {};
      this.dbg(
        `マイクOK ec=${(s as MediaTrackSettings).echoCancellation} ` +
          `ns=${(s as MediaTrackSettings).noiseSuppression}`
      );

      const { token, apiVersion, setup } = await this.opts.fetchToken();
      this.dbg(`トークン取得 ver=${apiVersion}`);
      await this.connect(token, apiVersion, setup);
      await this.startCapture();
      this.dbg("startCapture完了");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.emit("error", msg);
      this.setState("error");
      throw e;
    }
  }

  /** iOS 初回再生の遅延を消すため、無音を 1 回流してパイプラインを起こす */
  private warmUp(): void {
    if (!this.ctx) return;
    const buf = this.ctx.createBuffer(1, 1, OUTPUT_RATE);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);
    src.start();
  }

  private connect(
    token: string,
    apiVersion: string,
    setup: unknown
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const url =
        `wss://generativelanguage.googleapis.com/ws/` +
        `google.ai.generativelanguage.${apiVersion}.GenerativeService` +
        `.BidiGenerateContentConstrained?access_token=${encodeURIComponent(token)}`;

      const ws = new WebSocket(url);
      this.ws = ws;
      let settled = false;

      ws.onopen = () => {
        this.dbg("WS接続 → setup送信");
        // トークン発行時の setup と一致させる必要がある
        ws.send(JSON.stringify({ setup }));
      };

      ws.onmessage = (ev) => {
        const handle = (raw: string) => {
          const msg = this.parse(raw);
          if (!msg) return;
          if (msg.setupComplete && !settled) {
            settled = true;
            this.dbg("setupComplete");
            this.setState("ready");
            resolve();
            return;
          }
          this.handleMessage(msg);
        };
        if (ev.data instanceof Blob) void ev.data.text().then(handle);
        else handle(String(ev.data));
      };

      ws.onerror = () => {
        if (!settled) {
          settled = true;
          reject(new Error("WebSocket の接続に失敗しました"));
        } else {
          this.emit("error", "接続でエラーが発生しました");
        }
      };

      ws.onclose = (ev) => {
        this.dbg(`WS切断 code=${ev.code} ${ev.reason || ""}`.trim());
        if (!settled) {
          settled = true;
          reject(new Error("WebSocket が接続前に閉じられました"));
          return;
        }
        if (this._state !== "ended") {
          this.emit("ended", "disconnected");
          this.setState("ended");
        }
      };
    });
  }

  private parse(raw: string): Record<string, any> | null {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private async startCapture(): Promise<void> {
    if (!this.ctx || !this.stream) return;

    this.source = this.ctx.createMediaStreamSource(this.stream);
    this.sink = this.ctx.createGain();
    this.sink.gain.value = 0;
    this.sink.connect(this.ctx.destination);

    const code = `
      class Cap extends AudioWorkletProcessor {
        process(inputs) {
          const ch = inputs[0] && inputs[0][0];
          if (ch) this.port.postMessage(ch.slice(0));
          return true;
        }
      }
      registerProcessor('cap', Cap);
    `;
    const url = URL.createObjectURL(
      new Blob([code], { type: "application/javascript" })
    );
    await this.ctx.audioWorklet.addModule(url);
    URL.revokeObjectURL(url);

    this.worklet = new AudioWorkletNode(this.ctx, "cap");
    this.worklet.port.onmessage = (e: MessageEvent<Float32Array>) => {
      if (!this.sending || this.ws?.readyState !== WebSocket.OPEN) return;
      const pcm = toPCM16(e.data, this.ctx!.sampleRate, INPUT_RATE);
      this.ws.send(
        JSON.stringify({
          realtimeInput: {
            audio: {
              data: encodeBase64(new Uint8Array(pcm.buffer)),
              mimeType: `audio/pcm;rate=${INPUT_RATE}`,
            },
          },
        })
      );
    };
    this.source.connect(this.worklet);
    this.worklet.connect(this.sink);
  }

  // ---- push to talk -------------------------------------------------------

  /**
   * AI が話している間は呼んでも無視される。
   * 割り込むと生成がキャンセルされ、レイテンシが 1576ms まで悪化するため。
   */
  pressToTalk(): void {
    if (this._state !== "ready") return;
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sending = true;
    this.setState("listening");
  }

  releaseToTalk(): void {
    if (!this.sending) return;
    this.sending = false;
    this.releasedAt = Date.now();
    this.awaitingFirstAudio = true;
    this.setState("thinking");

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ realtimeInput: { audioStreamEnd: true } }));
    }

    // 応答が返らないターンが観測されている（9回中1回）。無応答を検知して復帰させる。
    this.armResponseTimeout();
  }

  private armResponseTimeout(): void {
    this.clearResponseTimeout();
    this.responseTimer = window.setTimeout(() => {
      if (this._state === "thinking") {
        this.emit("error", "応答がありませんでした。もう一度話しかけてください。");
        this.setState("ready");
      }
    }, RESPONSE_TIMEOUT_MS);
  }

  private clearResponseTimeout(): void {
    if (this.responseTimer !== null) {
      clearTimeout(this.responseTimer);
      this.responseTimer = null;
    }
  }

  // ---- incoming -----------------------------------------------------------

  private handleMessage(msg: Record<string, any>): void {
    const sc = msg.serverContent;
    if (!sc) return;

    if (sc.interrupted) {
      this.playHead = 0;
    }

    if (sc.inputTranscription?.text) {
      this.userBuffer += sc.inputTranscription.text;
      this.emit("transcriptUpdate", "user", this.userBuffer);
    }

    if (sc.outputTranscription?.text) {
      this.assistantBuffer += sc.outputTranscription.text;
      this.emit("transcriptUpdate", "assistant", this.assistantBuffer);
    }

    const parts = sc.modelTurn?.parts as Array<Record<string, any>> | undefined;
    if (parts) {
      let audioInThisMessage = 0;
      for (const part of parts) {
        const data = part.inlineData?.data;
        if (!data) {
          // 音声以外の part（テキスト等）が来た場合の切り分け用
          this.dbg(`part(非音声): ${Object.keys(part).join(",")}`);
          continue;
        }
        audioInThisMessage += 1;
        if (this.awaitingFirstAudio) {
          this.awaitingFirstAudio = false;
          this.clearResponseTimeout();
          this.emit("latency", Date.now() - this.releasedAt);
          this.setState("speaking");
        }
        this.play(decodeBase64(data));
      }
      if (audioInThisMessage > 0) {
        this.audioChunksThisTurn += audioInThisMessage;
        // 最初のチャンクだけ ctx 状態を出す（毎チャンク出すとログが溢れる）
        if (this.audioChunksThisTurn === audioInThisMessage) {
          this.dbg(`音声受信 ctx=${this.ctx?.state ?? "null"}`);
        }
      }
    }

    if (sc.turnComplete) {
      this.dbg(`turnComplete 音声チャンク=${this.audioChunksThisTurn}`);
      this.audioChunksThisTurn = 0;
      this.finishTurn();
    }
  }

  private finishTurn(): void {
    this.clearResponseTimeout();

    if (this.userBuffer.trim()) {
      const turn: Turn = {
        role: "user",
        text: this.userBuffer.trim(),
        turnIndex: this.turnIndex,
      };
      this.turns.push(turn);
      this.emit("turnComplete", turn);
    }
    if (this.assistantBuffer.trim()) {
      const turn: Turn = {
        role: "assistant",
        text: this.assistantBuffer.trim(),
        turnIndex: this.turnIndex,
      };
      this.turns.push(turn);
      this.emit("turnComplete", turn);
    }

    this.userBuffer = "";
    this.assistantBuffer = "";
    this.turnIndex += 1;
    this.emit("turnCountChange", this.turnIndex);

    // プロンプトでも「8往復で締める」と指示しているが、モデルが従わない場合がある。
    // クライアント側でも数えて強制終了させる二重の仕組み。
    if (this.turnIndex >= this.opts.maxTurns) {
      this.finish("max_turns");
      return;
    }

    this.setState("ready");
  }

  // ---- playback -----------------------------------------------------------

  private play(bytes: Uint8Array): void {
    if (!this.ctx) return;
    const count = Math.floor(bytes.byteLength / 2);
    const pcm = new Int16Array(bytes.buffer, bytes.byteOffset, count);
    const buf = this.ctx.createBuffer(1, count, OUTPUT_RATE);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < count; i++) ch[i] = pcm[i] / 32768;

    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);

    const now = this.ctx.currentTime;
    if (this.playHead < now) this.playHead = now + 0.02;
    src.start(this.playHead);
    this.playHead += buf.duration;
  }

  // ---- interruption -------------------------------------------------------

  /**
   * iOS では他アプリに移ると AudioContext が interrupted になる。
   * 復帰時に続きから再生されるが会話としては不自然なため、
   * 呼び出し側で「タップして再開」を挟むこと。この関数はその再開処理。
   */
  async resumeAudio(): Promise<boolean> {
    if (!this.ctx) return false;
    try {
      await this.ctx.resume();
      return this.ctx.state === "running";
    } catch {
      return false;
    }
  }

  isAudioSuspended(): boolean {
    if (!this.ctx) return false;
    return this.ctx.state !== "running";
  }

  /** 30秒以上離脱していた場合、中断された発話は破棄して仕切り直す */
  discardPendingAudio(): void {
    this.playHead = 0;
    this.assistantBuffer = "";
    if (this._state === "speaking" || this._state === "thinking") {
      this.setState("ready");
    }
  }

  // ---- teardown -----------------------------------------------------------

  finish(reason: "max_turns" | "user" | "error" = "user"): void {
    if (this._state === "ended") return;
    this.setState("ended");
    this.emit("ended", reason);
    this.cleanup();
  }

  private cleanup(): void {
    this.clearResponseTimeout();
    this.sending = false;

    try {
      this.worklet?.port.close();
      this.worklet?.disconnect();
      this.source?.disconnect();
      this.sink?.disconnect();
    } catch {
      /* noop */
    }

    this.stream?.getTracks().forEach((t) => t.stop());

    if (this.ws && this.ws.readyState <= WebSocket.OPEN) {
      this.ws.close();
    }

    void this.ctx?.close().catch(() => undefined);

    this.worklet = null;
    this.source = null;
    this.sink = null;
    this.stream = null;
    this.ws = null;
    this.ctx = null;
  }

  dispose(): void {
    this.cleanup();
    this.listeners = {};
  }
}

// ---- helpers --------------------------------------------------------------

/** 線形補間でリサンプルし、PCM16 に変換する。48kHz -> 16kHz で検証済み。 */
export function toPCM16(
  input: Float32Array,
  inRate: number,
  outRate: number
): Int16Array {
  const ratio = inRate / outRate;
  const length = Math.floor(input.length / ratio);
  const out = new Int16Array(length);
  for (let i = 0; i < length; i++) {
    const pos = i * ratio;
    const a = Math.floor(pos);
    const b = Math.min(a + 1, input.length - 1);
    let v = input[a] + (input[b] - input[a]) * (pos - a);
    v = v < -1 ? -1 : v > 1 ? 1 : v;
    out[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  return out;
}

export function encodeBase64(bytes: Uint8Array): string {
  let s = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(s);
}

export function decodeBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
